import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild, Optional } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { DxDataGridComponent, DxSelectBoxComponent } from 'devextreme-angular';
import { DataService } from '../core/data.service';
import { FormMode } from '../shared/enums';
import { ModalService } from '../shared/modal/modal.service';
import { ActiveModal } from '../shared/modal/modal.service';
import { RecurrentOrderItemFormComponent } from './recurrent-orderitem-form.component';
import { OrderTemplate } from '../shared/models/order-template';
import { RecurrencyPattern } from '../shared/models/recurrency-pattern';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/debounceTime';

@Component({
  selector: 'app-recurrent-order-form',
  templateUrl: 'recurrent-order-form.component.html',
  styleUrls: ['recurrent-order-form.component.css']
})
export class RecurrentOrderFormComponent implements AfterViewInit, OnInit {

  @ViewChild(NgForm) form: NgForm;
  @ViewChild(DxSelectBoxComponent) customerSelect: DxSelectBoxComponent;
  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;
  @Input() isStandalone: boolean;
  @Input() companyId: number;
  @Input() orderTemplate: OrderTemplate;
  @Output() orderTemplateSaved = new EventEmitter();
  orderTemplateModel: OrderTemplate;
  recurrencyPatternModel: RecurrencyPattern;
  formMode: FormMode;
  enum = { FormMode };
  customerSource: DataSource;
  customerFilter = new BehaviorSubject({});

  constructor(
    private notificationsService: NotificationsService,
    private modalService: ModalService,
    private dataService: DataService,
    private route: ActivatedRoute,
    @Optional() private activeModal: ActiveModal
  ) { }

  get customerKey() {
    return this.createCustomerKey({ id: this.orderTemplateModel.unloadPointID, position: this.orderTemplateModel.customerPos, customerId: this.orderTemplateModel.customerID });
  }

  set customerKey(key) {
    let val = key && JSON.parse(key);
    Object.assign(this.orderTemplateModel, {
      unloadPointID: val && val.id || null,
      customerID: val && val.customerId || null,
      customerPos: val && val.position || null
    });
    this.dataService.getCustomer(this.orderTemplateModel.unloadPointID, this.orderTemplateModel.customerPos, this.orderTemplateModel.customerID).then(c => {
      this.orderTemplateModel.customer = c;
    });
    this.form.form.markAsDirty();
  }

  ngOnInit() {
    this.orderTemplate = this.orderTemplate || this.route.snapshot.data['orderTemplate'];
    this.formMode = this.orderTemplate ? FormMode.Edit : FormMode.Create;
    this.prepareModel();

    this.customerSource = new DataSource(<any>{
      paginate: true,
      pageSize: 20,
      key: 'key',
      byKey: key => {
        let { id, position, customerId } = JSON.parse(key);
        return this.dataService.getCustomer(id, position, customerId).then(c => {
          this.orderTemplateModel.customer = c;
          return c;
        });
      },
      load: opts => {
        let f = this.customerFilter.getValue();
        opts = (({ skip, take }) => ({ skip, take }))(opts);
        opts.filter = Object.keys(f).reduce((obj, k) => f[k] ? (obj[k] = f[k]) && obj : obj, {});
        opts.filter.companyId = this.companyId;
        return this.dataService.getCustomers(opts);
      },
      map: customer => {
        customer.key = this.createCustomerKey(customer);
        return customer;
      }
    });

    this.customerFilter
      .debounceTime(300)
      .subscribe(value => {
        this.customerSelect.instance['_searchDataSource']();
      });
  }

  ngAfterViewInit() {
    this.dataGrid.instance.option({
      columnAutoWidth: true,
      editing: { texts: { confirmDeleteMessage: '' } },
      hoverStateEnabled: true,
      onRowRemoved: () => this.form.form.markAsDirty(),
      showBorders: true
    });

    this.customerSelect.instance.option({
      allowClearing: false,
      onOpened: e => {
        let popup = e.component._popup;
        if (!popup._$search) {
          let searchTemplate = e.component.option('integrationOptions.templates')['search'];
          if (searchTemplate) {
            let search = searchTemplate.render({ model: {} });
            popup._$search = search.prependTo(popup.overlayContent());

            let _setContentHeightOld = popup._setContentHeight;
            popup._setContentHeight = function() {
              _setContentHeightOld.call(this);
              if (!this._disallowUpdateContentHeight()) {
                if (this._$search && this._$search.is(':visible')) {
                  let popupContentHeight = parseInt(this.content().css('height'));
                  popupContentHeight -= this._$search.get(0).getBoundingClientRect().height || 0;
                  this.content().css({ height: popupContentHeight});
                }
              }
            };

            e.component._updatePopupWidth = function() {
              let width = this.element().get(0).getBoundingClientRect().width;
              this._setPopupOption('width', width + this.option('popupWidthExtension'));
            };

            e.component._refreshPopupVisibility = function() {
              if (this.option('opened')) {
                  this._dimensionChanged();
              }
            };

            e.component._dimensionChanged();
          }
        }
      }
    });
  }

  searchCustomers(value) {
    this.customerFilter.next(value);
  }

  prepareModel() {
    if (this.formMode === FormMode.Create) {
      this.orderTemplate = new OrderTemplate();
    }
    this.orderTemplate.recurrencyPattern = this.orderTemplate.recurrencyPattern || new RecurrencyPattern();
    this.orderTemplate.recurrencyPattern.changeDate = null;
    this.orderTemplateModel = { ...this.orderTemplate, orderItemTemplates: JSON.parse(JSON.stringify(this.orderTemplate.orderItemTemplates)) };
    this.recurrencyPatternModel = { ...this.orderTemplate.recurrencyPattern };
  }

  createCustomerKey(customer) {
    let keyProps = ['id', 'position', 'customerId'];
    let valid = customer && keyProps.every(prop => customer[prop]);
    return !valid ? null : JSON.stringify(keyProps.reduce((keyObj, prop) => (keyObj[prop] = customer[prop]) && keyObj, {}));
  }

  onSubmit(form) {
    if (this.isStandalone) {
      if (form.invalid) {
        this.notificationsService.error('Unable to create order', 'There are missing or invalid values');
        return;
      }
      this.orderTemplateModel.recurrencyPattern = { ...this.recurrencyPatternModel } ;
      if (this.formMode === FormMode.Edit) {
        this.dataService.updateOrderTemplate(this.orderTemplateModel).then(orderTemplate => {
          this.notificationsService.success('Template updated');
          this.orderTemplate = orderTemplate;
          this.orderTemplateModel = { ...this.orderTemplate };
          this.formMode = FormMode.Edit;
          form.form.markAsPristine();
          this.orderTemplateSaved.emit(orderTemplate);
          }, err => {
          let msg = err.status === 500 ? 'An error occurred' : 'There are missing or invalid values';
          this.notificationsService.error('Unable to update template', msg);
        });
      } else {
        this.dataService.createOrderTemplate(this.orderTemplateModel).then(orderTemplate => {
          this.notificationsService.success('Template created');
          this.orderTemplate = orderTemplate;
          this.orderTemplateModel = { ...this.orderTemplate };
          this.formMode = FormMode.Edit;
          form.form.markAsPristine();
          this.orderTemplateSaved.emit(orderTemplate);
        }, err => {
          let msg = err.status === 500 ? 'An error occurred' : 'There are missing or invalid values';
          this.notificationsService.error('Unable to create template', msg);
        });
      }
    } else {
      if (this.orderTemplateModel.customerID == null || this.orderTemplateModel.customerID < 1) {
        this.notificationsService.error('Missing Value', 'Select the customer');
        return;
      }
      this.orderTemplate = { ...this.orderTemplateModel, recurrencyPattern: { ...this.recurrencyPatternModel }, customer: { ...this.orderTemplateModel.customer}  };
      this.orderTemplateSaved.emit(this.orderTemplate);
      this.activeModal.dismiss();
    }
  }

  revertChanges(form) {
    /*let clonedTemplate = JSON.parse(JSON.stringify(this.orderTemplate));
    this.orderTemplateModel = clonedTemplate;
    this.recurrencyPatternModel = clonedTemplate.recurrencyPattern;
    this.customerSelect.value = this.createCustomerKey({ id: clonedTemplate.customerID, position: clonedTemplate.customerPos });
    form.form.markAsPristine();*/
    this.activeModal.dismiss();
  }

  showProductModal() {
    let modal = this.modalService.open(RecurrentOrderItemFormComponent);
    modal.componentInstance.addedItem.subscribe(orderItemTemplate => {
      console.log(orderItemTemplate);
      orderItemTemplate.orderTemplateID = this.orderTemplateModel.id;
      if (orderItemTemplate.qty == null || orderItemTemplate.qty == '') {
        orderItemTemplate.qty = 0;
      }
      if (orderItemTemplate.price == null || orderItemTemplate.price == '') {
        orderItemTemplate.price = 0;
      }
      this.orderTemplateModel.orderItemTemplates.push(orderItemTemplate);
      this.form.form.markAsDirty();
    });
  }
  onRowUpdated(e) {
    if (e.key.qty == null) {
        e.key.qty = 0;
    }
    if (e.key.price == null) {
      e.key.price = 0;
  }
  }
  onCellPrepared(e) {
    if (e.rowType === 'header' && e.column.command === 'edit') {
     /*
      let headerElement = e.cellElement;
      let template = '<div style="text-align: center;"><i class="fa fa-plus fa-lg" aria-hidden="true" onclick="this.showProductModal()"></i></div>';
      headerElement.html(template);*/

    } else if (e.rowType === 'data' && e.column.command === 'edit') {
        let cellElement = e.cellElement;
        let deleteLink = cellElement[0].querySelector('.dx-link-delete');
        // deleteLink.classList.add('dx-icon-remove');
        if (deleteLink) {
        deleteLink.textContent = 'Remove';
        }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/recurrence/recurrent-order-form.component.ts